/**
 * useUserSubscriptions Hook
 *
 * 管理用户级 WebSocket 订阅（层级 1）。
 * 在用户登录且 WebSocket 已连接时，自动订阅所有 7 个核心 TaskType 房间。
 * 监听重连事件，断线重连后自动恢复所有订阅。
 * 订阅失败时静默重试最多 3 次，不阻塞页面渲染。
 *
 * 需求：1.1、1.2、1.3、1.5、1.6、1.7
 */

import { useEffect, useRef, useState } from 'react';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';
import { useAuthStore } from '../stores/authStore';
import { TaskType } from '../services/websocket/types';

/** 用户级订阅的核心 TaskType */
const USER_TASK_TYPES: TaskType[] = [
  TaskType.IMAGE_GENERATION,
  TaskType.VIDEO_GENERATION,
  TaskType.AUDIO_GENERATION,
  TaskType.VIDEO_LIPSYNC,
  TaskType.MUSIC_GENERATION,
  TaskType.MUSIC_EDITING,
  TaskType.AUDIO_EDITING,
  TaskType.IMAGE_EDIT,
];

const MAX_RETRY = 3;

export interface UseUserSubscriptionsReturn {
  /** 用户级订阅是否已全部就绪 */
  isReady: boolean;
}

/**
 * 订阅单个 taskType，失败时最多重试 MAX_RETRY 次
 */
async function subscribeWithRetry(
  client: NonNullable<ReturnType<typeof useWebSocketContext>['client']>,
  taskType: TaskType
): Promise<void> {
  let lastError: unknown;
  for (let attempt = 0; attempt < MAX_RETRY; attempt++) {
    try {
      await client.subscribe({ type: 'taskType', taskType });
      return;
    } catch (err) {
      lastError = err;
    }
  }
  // 静默处理：记录但不抛出
  console.warn(`[useUserSubscriptions] 订阅 taskType:${taskType} 失败（已重试 ${MAX_RETRY} 次）`, lastError);
}

export function useUserSubscriptions(): UseUserSubscriptionsReturn {
  const { client, isConnected } = useWebSocketContext();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const [isReady, setIsReady] = useState(false);

  // 用 ref 追踪当前是否正在订阅中，避免并发重复执行
  const subscribingRef = useRef(false);

  useEffect(() => {
    // 条件不满足时重置 isReady
    if (!isConnected || !token || !user || !client) {
      setIsReady(false);
      return;
    }

    // 防止并发重复订阅
    if (subscribingRef.current) return;
    subscribingRef.current = true;

    let cancelled = false;

    const subscribeAll = async () => {
      try {
        // 并发订阅所有 taskType，各自独立重试
        await Promise.all(
          USER_TASK_TYPES.map((taskType) => subscribeWithRetry(client, taskType))
        );

        if (!cancelled) {
          setIsReady(true);
        }
      } catch {
        // subscribeWithRetry 内部已静默处理，此处不会抛出
      } finally {
        subscribingRef.current = false;
      }
    };

    subscribeAll();

    return () => {
      cancelled = true;
      subscribingRef.current = false;
    };
    // isConnected 变化（包括重连）时重新执行
  }, [isConnected, token, user, client]);

  return { isReady };
}
